import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { useEffect, useState } from "react";
import {
  readCart,
  removeFromCart,
  formatPrice,
  type CartItem,
} from "@/lib/store";
import { useServerFn } from "@tanstack/react-start";
import { createCheckoutSession } from "@/lib/store.functions";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export const Route = createFileRoute("/store/cart")({
  component: CartPage,
});

function CartPage() {
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const checkout = useServerFn(createCheckoutSession);

  useEffect(() => {
    setItems(readCart());
    const onUpdate = () => setItems(readCart());
    window.addEventListener("ck:cart-updated", onUpdate);
    return () => window.removeEventListener("ck:cart-updated", onUpdate);
  }, []);

  const subtotal = items.reduce((s, i) => s + i.price_cents, 0);

  async function onCheckout() {
    if (items.length === 0) return;
    if (!user) return;
    setLoading(true);
    try {
      const { url } = await checkout({
        data: {
          productIds: items.map((i) => i.product_id),
        },
      });
      if (url) window.location.href = url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Checkout failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <section className="mx-auto max-w-3xl px-6 py-20">
        <Eyebrow>Your cart</Eyebrow>
        <h1 className="mt-4 font-serif text-4xl text-navy-900">Review your library</h1>

        {items.length === 0 ? (
          <div className="mt-10 rounded-sm border border-bronze-600/30 bg-white p-12 text-center">
            <p className="font-sans text-charcoal-500">Your cart is empty.</p>
            <Link to="/store" className="mt-4 inline-block font-sans text-bronze-600 underline">
              Browse the store
            </Link>
          </div>
        ) : (
          <>
            <ul className="mt-10 divide-y divide-bronze-600/20 rounded-sm border border-bronze-600/20 bg-white">
              {items.map((i) => (
                <li key={i.product_id} className="flex items-center justify-between gap-6 p-6">
                  <div>
                    <h3 className="font-serif text-lg text-navy-800">{i.title}</h3>
                    <button
                      onClick={() => removeFromCart(i.product_id)}
                      className="mt-2 font-sans text-xs uppercase tracking-[0.22em] text-charcoal-500 hover:text-danger"
                    >
                      Remove
                    </button>
                  </div>
                  <p className="font-serif text-xl text-navy-900">
                    {formatPrice(i.price_cents)}
                  </p>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex items-center justify-between border-t border-bronze-600/30 pt-6">
              <p className="font-sans text-sm uppercase tracking-[0.22em] text-charcoal-500">
                Subtotal
              </p>
              <p className="font-serif text-3xl text-navy-900">
                {formatPrice(subtotal)}
              </p>
            </div>

            {user ? (
              <>
                <div className="mt-8">
                  <PrimaryButton onClick={onCheckout} disabled={loading}>
                    {loading ? "Redirecting…" : "Continue to secure checkout"}
                  </PrimaryButton>
                </div>
                <p className="mt-4 font-sans text-xs text-charcoal-500">
                  Your purchase is delivered to your Library the moment payment
                  clears. All digital sales are final.
                </p>
              </>
            ) : (
              <div className="mt-8 rounded-sm border border-bronze-600/30 bg-white p-6">
                <h3 className="font-serif text-xl text-navy-900">
                  Sign in to complete your purchase
                </h3>
                <p className="mt-2 font-sans text-sm text-charcoal-500">
                  Your library is tied to your account, so we ask you to sign in
                  before checkout. Your cart is saved — you will land right back
                  here.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <a href="/login?redirect=/store/cart">
                    <PrimaryButton>Sign in and check out</PrimaryButton>
                  </a>
                  <a
                    href="/signup?redirect=/store/cart"
                    className="font-sans text-sm text-bronze-600 underline"
                  >
                    Create an account
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}